import React, { useState } from 'react';
import PropTypes from 'prop-types';

import arrowForward from '../../../../image/Icons/arrow-forward.svg';
import doubleTick from '../../../../image/Icons/double-tick.svg';
import tick from '../../../../image/Icons/tick.svg';
import InfoTooltip from '../../compat/InfoTooltip';

import './PackageCard.css';

/**
 * PackageCard.
 * Component for handling product package selection.
 *
 * @author  Manny
 * @param   {String}      props.method            Optional industry params.
 * @param   {Function}    props.onSelectPackage   Handles onClick for selecting a package.
 * @param   {Number}      props.packageIndex      Number of package index.
 * @param   {String}      props.selectedIndustry  Name of selected industry.
 * @param   {Object}      props.singlePackage     Name of selected package.
 * @param   {Boolean}     props.isMobile          Determines if tables are for mobile format.
 * @param   {Object}      props.oneMonth          Name of lite package in monthly billing.
 * @returns {JSX.Element} The component markup.
 */

const PackageCard = ({
  method,
  onSelectPackage,
  packageIndex,
  selectedIndustry,
  singlePackage,
  isMobile,
  oneMonth
}) => {


  const [liteBilling, setLiteBilling] = useState(1);
  let classNames = require('classnames');
  let trialLinkClass = classNames({
    'trial-container captions button-trial full-width': (method ? !method.includes('buyNow') : true) && packageIndex === 2,
    '': (method ? method.includes('buyNow') : false) && packageIndex === 2
  });
  let colSize = classNames({
    'col-sm-3 no-padding card-column': !isMobile,
    'col-sm-4 no-right-padding exp mobile-card-column': isMobile
  });
  let liteMonthlyBillingClass = classNames({
    'lite-btn lite-monthly-active captions col-xs-6':   packageIndex === 2 && liteBilling === 2,
    'lite-btn lite-monthly captions col-xs-6':  packageIndex === 2 && liteBilling === 1,
  })
  let liteYearlyBillingClass = classNames({
    'lite-btn lite-yearly captions col-xs-6': packageIndex === 2 && liteBilling === 2,
    'lite-btn lite-yearly-active captions col-xs-6': packageIndex === 2 && liteBilling === 1,
  })

  const handleOnClickYearly = () =>{
    setLiteBilling(1)
  }

  const handleOnClickMonthly = () =>{
    setLiteBilling(2)
  }

const doubleTickText = {
  base:    "Everything in Lite plus",
  premium: "Everything in Base plus",
  pro:     "Everything in Premium plus"
} [singlePackage.key] || "";

  const isPremium = singlePackage.key === 'premium';
  const isLitePackage = packageIndex === 2;
  const isMonthly = liteBilling === 2;
  const isAnnually = liteBilling === 1;
  const isLite = isLitePackage;
  const packageData = isLite && isMonthly ? oneMonth : singlePackage;
  const showPrice = isLitePackage;
  const superScriptOne = <sup> 1 </sup>;
  const superScriptTwo = <sup> 2 </sup>;

  const renderTitle = (title, description) => {
    return <>
      <div className="flex">
        <div className="column title-column">
          {title}
          {title.includes('RP Inside Connection') && superScriptOne}
          {title.includes('10,000 Property Exports per Month') && superScriptTwo}
        </div>
        <div className="column tooltip-column text-right">
        {description &&
          <InfoTooltip text={description}>
            <span style={{
              alignItems: "center",
              border: "1px solid #79848b",
              borderRadius: "50%",
              cursor: "pointer",
              display: "flex",
              fontFamily: "TWK Everett, sans-serif",
              fontSize: "11px",
              height: "15px",
              justifyContent: "center",
              marginTop: "5px",
              width: "15px"
            }}>
              i
            </span>
          </InfoTooltip>
        }
        </div>
      </div>
    </>
  }

  return (
    <>
      <div id={isPremium ? "best-value-column" : ""} className={colSize} key={packageIndex}>
        <div className={`panel-container package-card ${isPremium ? "best-value-card" : ""}`}>
          {isPremium && <div id="best-value-caption" className='best-value-tag captions'><b>BEST VALUE</b></div>}
          <p
            id={`package_title_${singlePackage.key}`}
            className='package-card-title'
          >
            {singlePackage.title}
          </p>
          <p id={`package-blurb-${singlePackage.key}`} className="package-blurb">
            {singlePackage.blurb}
          </p>

          {isLite ?
            <>
            <div className='lite-container'>
              <div id="annual-button" className={liteYearlyBillingClass} onClick={handleOnClickYearly}>
                Annual
              </div>
              <div id="monthly-tab-button" className={liteMonthlyBillingClass} onClick={handleOnClickMonthly}>
                Monthly
              </div>
            </div>
            </>
            : (<>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>
              Yearly Plan
            </div>
            <div style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>
              Billed Monthly
            </div>
            <p className='heading2' style={{minHeight: "40px"}}>
              {
                (<>
                <br/>
                <span style={{lineHeight: "32px", fontWeight: 700, fontSize: "30px"}}><b>${singlePackage.base_price}</b></span>
                <span className='captions'>/MTH</span>
                </>
                )
              }
            </p>
            </>)
          }
          { showPrice && (
              <>
                <p className="monthly-annual-price-text">
                  <span style={{fontWeight: 700, fontSize: "30px"}}>
                    ${packageData?.base_price}
                  </span>
                  <span className="footnote-text" style={{lineHeight: "15px", fontSize: "14px", fontWeight: 400}}>
                    /MTH
                  </span>
                </p>
              </>
            )}
          { packageData && <>
            <p className='package-sub-text padding-top-5 footnote-text' style={{color: "#1e1405", fontSize: "10px", opacity: 0.6, marginTop: showPrice ? "10px" : undefined}}>
              {packageData.sub_text_1}
            </p>
            <p className={`package-sub-text margin-bottom-${(isMonthly || isAnnually) ? '10':'20'} footnote-text`} style={{color: "#1e1405", fontSize: "10px", opacity: 0.6}}>
              {packageData.sub_text_2}
            </p>
          </>}

          {/* Select Package Button */}
          { packageData &&
            <button
              id={`select_package_btn_${isLite && isMonthly ? oneMonth.key : singlePackage.key}`}
              type='button'
              className={`btn btn-arrow-move btn-select-package full-width no-margin ${isPremium ? "best-value-btn" : ""}`}
              onClick={() => {
                onSelectPackage(selectedIndustry, isLite && isMonthly ? oneMonth.key : singlePackage.key);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                width: '100%',
                padding: '12px 24px',
              }}
            >
              Select Package
              <img src={arrowForward} alt='>' className='arrow-forward'/>
            </button>
          }
          {/* End Select Package Button */}
          <hr/>
          {isLite && isMonthly?
          oneMonth?.features?.filteredFeatures && (
            <div className='features-list'>
              {oneMonth.features.filteredFeatures.map((feature, featureKey) => {
                if (feature) {
                  return (
                    <div className='features-container body' key={featureKey}>
                      <div>
                        <img src={tick} alt='yes' className='margin-right-5 tick-margin' />
                      </div>
                      <div>
                        {renderTitle(feature.title, feature.description)}
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          )
          : isLite && isAnnually ?
          oneMonth?.features?.filteredFeatures && (
            <div className='features-list'>
              {oneMonth.features.filteredFeatures.map((feature, featureKey) => {
                if (feature) {
                  return (
                    <div className='features-container body' key={featureKey}>
                      <div>
                        <img src={tick} alt='yes' className='margin-right-5 tick-margin' />
                      </div>
                      <div>
                        {renderTitle(feature.title, feature.description)}
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          )
          :
            singlePackage?.features?.filteredFeatures && (
              <div className='features-list'>
                <div className='features-container body'>
                  <div>
                    <img src={doubleTick} alt='yes' className='margin-right-5 tick-margin' />
                  </div>
                  <div>
                    <b>{doubleTickText}</b>
                  </div>
                </div>
                {singlePackage.features.filteredFeatures.map((feature, featureKey) => {
                  if (feature) {
                    return (
                      <div className='features-container body' key={featureKey}>
                        <div>
                          <img src={tick} alt='yes' className='margin-right-5 tick-margin' />
                        </div>
                        <div>
                          {renderTitle(feature.title, feature.description)}
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
            )
          }
        </div>
      </div>
    </>
  );
};

PackageCard.propTypes = {
  method: PropTypes.string,
  onSelectPackage: PropTypes.func,
  packageIndex: PropTypes.number.isRequired,
  selectedIndustry: PropTypes.string.isRequired,
  singlePackage: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired,
  oneMonth: PropTypes.object.isRequired
};

export default PackageCard;
